import * as React from "react";

import { Box, Flex, Heading, Text, Button, HStack, Link, Table, Spinner } from "@libs/design";
import { Download } from "@assets/icons";
import { BASE_URL } from "@shared/constants";

import { StatusBadge } from "../../status-badge";
import { SeverityBadge } from "../../severity-badge";
import { useGetTickets, type TicketItem } from "../../../api";
import { TICKETS_EXPORT_URL } from "../../../api/apiEndpoints";
import { formatDateTime } from "../../../utils";

type TicketRow = {
  ticket: string;
  alertId: number;
  country: string;
  company: string;
  category: string;
  severity: string;
  created: string;
  status: string;
  sla: string;
  snowUrl: string;
};

export const TicketsTable = () => {
  const colors = {
    textPrimary: "#111827",
    textSecondary: "#6B7280",
    link: "#00927B",
    border: "#E5E7EB",
  };

  // API call
  const { data, isLoading, isError } = useGetTickets();

  // Map API response to table format
  const tickets: TicketRow[] = React.useMemo(() => {
    const apiTickets: TicketItem[] = Array.isArray(data?.data) ? data.data : [];

    const toNumber = (value: unknown): number => {
      if (typeof value === "number" && !Number.isNaN(value)) return value;
      if (typeof value === "string" && value.trim() !== "") {
        const parsed = Number(value);
        if (!Number.isNaN(parsed)) return parsed;
      }
      return Number.MAX_SAFE_INTEGER;
    };

    const pickString = (...values: unknown[]): string => {
      for (const value of values) {
        if (typeof value === "string" && value.trim() !== "") return value;
      }
      return "N/A";
    };

    const normalizeSeverity = (raw: string): string => {
      if (!raw) return "N/A";
      // API severities like "2 - High", "3 - Moderate" -> "High", "Moderate"
      const parts = raw.split("-").map((p) => p.trim());
      return parts[1] || parts[0] || raw;
    };

    return apiTickets
      .map((item: TicketItem) => ({
        ticket: pickString(item.ticketNumber, item.ticket_number),
        alertId: toNumber(item.alertId ?? item.alert_id),
        country: item.country || "N/A",
        company: pickString(item.companycode, item.company_code),
        category: item.category ?? "N/A",
        severity: normalizeSeverity(item.severity),
        created: item.createdAt,
        status: item.status,
        sla: item.slaRemaining,
        snowUrl: item.snowUrl,
      }))
      .sort((a, b) => {
        const byTicket = a.ticket.localeCompare(b.ticket, undefined, {
          numeric: true,
          sensitivity: "base",
        });
        if (byTicket !== 0) return byTicket;
        return a.alertId - b.alertId;
      });
  }, [data]);

  return (
    <Box bg="white" borderRadius="xl" borderWidth="1px" boxShadow="sm" overflow="hidden">
      {/* Header */}
      <Box p={6} borderBottomWidth="1px" borderColor="gray.100">
        <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
          <Box>
            <Heading fontSize="lg" fontWeight="semibold" color="gray.900">
              ServiceNow Tickets
            </Heading>
            <Text fontSize="sm" color="gray.500">
              Integrated ticket tracking
            </Text>
          </Box>
          <HStack>
            <Box as="span" color="gray.600" display="inline-flex" alignItems="center">
              <Download size={16} color="#00927B" />
            </Box>
            <Button
              variant="outline"
              onClick={() => {
                const url = `${BASE_URL}${TICKETS_EXPORT_URL}`;
                window.open(url, "_blank", "noopener,noreferrer");
              }}
            >
              Export Report
            </Button>
          </HStack>
        </Flex>
      </Box>

      {/* Body */}
      <Box overflowX="auto">
        {isLoading ? (
          <Flex justify="center" align="center" p={10}>
            <Spinner />
          </Flex>
        ) : isError ? (
          <Flex justify="center" align="center" p={10}>
            <Text color="red.500">Failed to load tickets</Text>
          </Flex>
        ) : (
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader fontSize="xs" textTransform="uppercase" px={6} py={3}>
                  Ticket #
                </Table.ColumnHeader>
                <Table.ColumnHeader fontSize="xs" textTransform="uppercase" px={6} py={3}>
                  Country
                </Table.ColumnHeader>
                <Table.ColumnHeader fontSize="xs" textTransform="uppercase" px={6} py={3}>
                  Category
                </Table.ColumnHeader>
                <Table.ColumnHeader fontSize="xs" textTransform="uppercase" px={6} py={3}>
                  Severity
                </Table.ColumnHeader>
                <Table.ColumnHeader fontSize="xs" textTransform="uppercase" px={6} py={3}>
                  Created
                </Table.ColumnHeader>
                <Table.ColumnHeader fontSize="xs" textTransform="uppercase" px={6} py={3}>
                  Status
                </Table.ColumnHeader>
                <Table.ColumnHeader fontSize="xs" textTransform="uppercase" px={6} py={3}>
                  SLA
                </Table.ColumnHeader>
                <Table.ColumnHeader fontSize="xs" textTransform="uppercase" px={6} py={3}>
                  Action
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {tickets.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={8} textAlign="center" py={8}>
                    <Text color="gray.500">No tickets found</Text>
                  </Table.Cell>
                </Table.Row>
              ) : (
                tickets.map((ticket, i) => (
                  <Table.Row key={i} _hover={{ bg: "gray.50" }}>
                    <Table.Cell
                      px={6}
                      py={4}
                      fontSize="sm"
                      fontWeight="medium"
                      style={{ color: colors.link }}
                    >
                      {ticket.ticket}
                    </Table.Cell>

                    <Table.Cell px={6} py={4} fontSize="sm">
                      {ticket.country}
                    </Table.Cell>

                    <Table.Cell px={6} py={4} fontSize="sm" color="gray.600">
                      {ticket.category}
                    </Table.Cell>

                    <Table.Cell px={6} py={4}>
                      <SeverityBadge severity={ticket.severity} />
                    </Table.Cell>

                    <Table.Cell px={6} py={4} fontSize="sm" color="gray.500">
                      {formatDateTime(ticket.created)}
                    </Table.Cell>

                    <Table.Cell px={6} py={4}>
                      <StatusBadge status={ticket.status} />
                    </Table.Cell>

                    <Table.Cell px={6} py={4}>
                      <Text fontSize="sm" fontWeight="medium" color="blue.800">
                        {ticket.sla}
                      </Text>
                    </Table.Cell>

                    <Table.Cell px={6} py={4}>
                      <Link
                        href={ticket.snowUrl}
                        target="_blank"
                        color="green.500"
                        fontWeight="bold"
                      >
                        Open in SNOW
                      </Link>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Root>
        )}
      </Box>
    </Box>
  );
};
